import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { CalendarStar, Fire, Trophy, Warning, Check, Users, Sparkle, ShieldCheck } from "@phosphor-icons/react";
import api, { apiErr } from "../api";
import GoldCounter from "../components/GoldCounter";

const OUTCOME_LABEL = {
  critical_success: "Critical Success", success: "Success", narrow_escape: "Narrow Escape",
  failure: "Failure", major_loss: "Major Loss",
};

export default function Daily() {
  const nav = useNavigate();
  const [daily, setDaily] = useState(null);
  const [crew, setCrew] = useState([]);
  const [selected, setSelected] = useState([]);
  const [board, setBoard] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = () => {
    Promise.all([api.get("/daily"), api.get("/crew"), api.get("/daily/leaderboard")])
      .then(([d, c, l]) => {
        setDaily(d.data);
        setCrew(c.data);
        setBoard(l.data.entries || []);
      })
      .catch((e) => toast.error(apiErr(e)));
  };
  useEffect(() => { load(); }, []);

  const toggle = (id) =>
    setSelected((p) => (p.includes(id) ? p.filter((x) => x !== id) : p.length >= 3 ? p : [...p, id]));

  const attempt = async (mode) => {
    if (selected.length !== 3) return toast.error("Select exactly three crew members");
    setBusy(true);
    try {
      const { data } = await api.post("/daily/attempt", { mode, crew_ids: selected });
      toast.success(mode === "ranked" ? "Ranked stake reserved. Begin!" : "Practice run started.");
      nav(`/mission/${data.id}`);
    } catch (e) {
      toast.error(apiErr(e));
      setBusy(false);
    }
  };

  if (!daily) {
    return <div className="py-20 text-center text-ff-muted" data-testid="daily-loading">Charting today's expedition…</div>;
  }

  const d = daily.daily;
  const rankedDone = daily.ranked_status === "completed";
  const rankedAvailable = daily.ranked_status === "available";
  const meta = d.meta || {};

  return (
    <div className="space-y-6" data-testid="daily-page">
      <div className="flex items-center gap-2">
        <CalendarStar size={26} weight="duotone" className="text-ff-gold" />
        <h1 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">Daily Expedition</h1>
      </div>

      {/* Streak + status strip */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div className="ff-panel rounded-md p-4" data-testid="daily-streak">
          <div className="flex items-center gap-2 text-ff-brass"><Fire size={18} weight="duotone" /><span className="text-xs uppercase tracking-widest">Current Streak</span></div>
          <div className="mt-1 font-display text-3xl font-bold text-ff-gold">{daily.streak.current_streak} <span className="text-base text-ff-muted">days</span></div>
          <div className="text-xs text-ff-muted">Best: {daily.streak.longest_streak} · Milestones at 3 · 7 · 14 · 30</div>
        </div>
        <div className="ff-panel rounded-md p-4" data-testid="daily-stake-panel">
          <div className="flex items-center gap-2 text-ff-brass"><ShieldCheck size={18} weight="duotone" /><span className="text-xs uppercase tracking-widest">Ranked Stake</span></div>
          <div className="mt-1 font-display text-3xl font-bold text-ff-gold">{daily.stake} <span className="text-base text-ff-muted">Gold</span></div>
          <div className="text-xs text-ff-muted">Balance: <GoldCounter value={daily.balance} /> · One ranked attempt / day</div>
        </div>
        <div className="ff-panel rounded-md p-4" data-testid="daily-status-panel">
          <div className="flex items-center gap-2 text-ff-brass"><Sparkle size={18} weight="duotone" /><span className="text-xs uppercase tracking-widest">Today's Status</span></div>
          <div className="mt-1 font-display text-2xl font-bold text-ff-gold" data-testid="daily-ranked-status">
            {rankedDone ? `Completed — ${OUTCOME_LABEL[daily.ranked_result] || "Done"}` : rankedAvailable ? "Ranked Available" : "In Progress"}
          </div>
          <div className="text-xs text-ff-muted">Practice replays are always free</div>
        </div>
      </div>

      {/* Briefing */}
      <div className="ff-panel overflow-hidden rounded-md">
        <div className="relative h-40 sm:h-52">
          <img src={d.image} alt={d.name} className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-ff-bg2 to-transparent" />
          <div className="absolute bottom-3 left-4">
            <p className="font-mono-ff text-[10px] uppercase tracking-widest text-ff-brass">{d.region} · {d.date}</p>
            <h2 className="font-display text-2xl font-semibold text-ff-parchment">{d.name}</h2>
          </div>
        </div>
        <div className="p-5">
          <p className="text-sm text-ff-muted">{d.description}</p>
          <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2">
            {[
              ["Environment", meta.environment],
              ["Primary Objective", meta.primary_objective],
              ["Secondary Objective", meta.secondary_objective],
              ["Crew Complication", meta.complication],
              ["Strategic Modifier", meta.modifier],
              ["Reward Modifier", meta.reward_modifier],
            ].map(([label, m]) => m && (
              <div key={label} className="rounded-sm border border-ff-brass/20 bg-ff-bg/50 px-3 py-2" data-testid={`daily-meta-${label.split(" ")[0].toLowerCase()}`}>
                <div className="text-[10px] uppercase tracking-widest text-ff-brass">{label}</div>
                <div className="text-sm text-ff-parchment">{m.label}</div>
                {m.desc && <div className="text-xs text-ff-muted">{m.desc}</div>}
              </div>
            ))}
          </div>
          <div className="mt-3 flex items-center gap-2 text-xs text-ff-muted">
            <span className="rounded-sm bg-ff-gold/15 px-2 py-0.5 text-ff-gold">Skill: {meta.skill_challenge?.label}</span>
            <span className="rounded-sm bg-ff-brass/15 px-2 py-0.5 text-ff-brass">Skills: {d.primary_skills.join(", ")}</span>
          </div>
          <div className="mt-3 flex items-start gap-1.5 rounded-sm bg-ff-danger/10 px-2 py-1.5 text-xs text-ff-danger/90">
            <Warning size={14} className="mt-0.5 shrink-0" /><span>{d.main_risk} You can never lose more than your confirmed stake.</span>
          </div>
        </div>
      </div>

      {/* Crew select */}
      <div>
        <div className="mb-2 flex items-center gap-2">
          <Users size={20} weight="duotone" className="text-ff-gold" />
          <h3 className="font-display text-xl font-semibold">Select Your Crew · <span data-testid="daily-crew-count" className="text-ff-gold">{selected.length}/3</span></h3>
        </div>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
          {crew.map((c) => {
            const active = selected.includes(c.id);
            const disabled = !c.available;
            return (
              <button
                key={c.id}
                disabled={disabled}
                onClick={() => !disabled && toggle(c.id)}
                data-testid={`daily-crew-${c.id}`}
                className={`ff-panel relative rounded-md p-3 text-left transition-all ${disabled ? "cursor-not-allowed opacity-50" : "hover:-translate-y-0.5"} ${active ? "border-ff-gold ff-glow-gold" : ""}`}
              >
                {active && <span className="absolute right-2 top-2 flex h-5 w-5 items-center justify-center rounded-full bg-ff-gold text-ff-bg"><Check size={12} weight="bold" /></span>}
                <img src={c.portrait} alt={c.name} className="h-14 w-14 rounded-sm object-cover" />
                <div className="mt-1 text-sm font-medium leading-tight text-ff-parchment">{c.name.split(" ")[0]}</div>
                <div className="text-[10px] text-ff-muted">Lv {c.level}{disabled ? " · unavailable" : ""}</div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Actions */}
      <div className="flex flex-col gap-3 sm:flex-row">
        <button
          onClick={() => attempt("ranked")}
          disabled={busy || selected.length !== 3 || !daily.ranked_eligible || !rankedAvailable}
          data-testid="daily-ranked-btn"
          className="flex flex-1 items-center justify-center gap-2 rounded-sm bg-ff-gold py-3.5 font-semibold text-ff-bg transition-all hover:bg-ff-brass active:scale-95 disabled:cursor-not-allowed disabled:opacity-40"
        >
          <Trophy size={18} weight="duotone" />
          {rankedDone ? "Ranked Completed Today" : `Play Ranked (${daily.stake} Gold)`}
        </button>
        <button
          onClick={() => attempt("practice")}
          disabled={busy || selected.length !== 3}
          data-testid="daily-practice-btn"
          className="flex flex-1 items-center justify-center gap-2 rounded-sm border border-ff-brass/40 py-3.5 font-semibold text-ff-parchment transition-colors hover:border-ff-gold hover:text-ff-gold active:scale-95 disabled:opacity-40"
        >
          <Sparkle size={18} /> Practice (Free)
        </button>
      </div>
      {rankedAvailable && !daily.ranked_eligible && daily.ineligible_reason && (
        <p className="text-center text-xs text-ff-danger/90" data-testid="daily-ineligible">{daily.ineligible_reason}. Practice mode is still available.</p>
      )}

      {/* Leaderboard */}
      <div>
        <div className="mb-2 flex items-center gap-2">
          <Trophy size={20} weight="duotone" className="text-ff-gold" />
          <h3 className="font-display text-xl font-semibold">Today's Daily Leaderboard</h3>
        </div>
        <div className="ff-panel overflow-hidden rounded-md" data-testid="daily-leaderboard">
          {board.length === 0 ? (
            <div className="p-6 text-center text-sm text-ff-muted">No ranked explorers yet today. Be the first to complete it.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="border-b border-ff-brass/20 text-left text-xs uppercase tracking-widest text-ff-brass">
                <tr>
                  <th className="px-3 py-2">#</th><th className="px-3 py-2">Explorer</th>
                  <th className="px-3 py-2">Outcome</th><th className="px-3 py-2 text-right">Score</th>
                  <th className="hidden px-3 py-2 text-right sm:table-cell">Skill</th>
                  <th className="hidden px-3 py-2 text-right sm:table-cell">Crew</th>
                </tr>
              </thead>
              <tbody>
                {board.map((r) => (
                  <tr key={r.user_id} className="border-b border-ff-brass/10" data-testid={`daily-lb-row-${r.position}`}>
                    <td className="px-3 py-2 font-mono-ff text-ff-gold">{r.position}</td>
                    <td className="px-3 py-2 text-ff-parchment">{r.username}</td>
                    <td className="px-3 py-2 text-ff-muted">{OUTCOME_LABEL[r.outcome] || r.outcome}</td>
                    <td className="px-3 py-2 text-right font-medium text-ff-parchment">{r.final_score}</td>
                    <td className="hidden px-3 py-2 text-right text-ff-muted sm:table-cell">{r.skill_score}</td>
                    <td className="hidden px-3 py-2 text-right text-ff-muted sm:table-cell">{r.crew_survival}/3</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
